import { makeStyles } from "@material-ui/core/styles";
import React, { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  scrollbar: {
    position: "fixed",
    bottom: 0,
    height: 16,
    overflowX: "auto",
    overflowY: "hidden",
    zIndex: 2,
    backgroundColor: theme.palette.background.paper,
    scrollbarWidth: "auto",
    scrollbarColor: `${theme.palette.grey[500]} ${theme.palette.background.paper}`,
    "&::-webkit-scrollbar": { height: 12 },
    "&::-webkit-scrollbar-track": { backgroundColor: theme.palette.background.paper },
    "&::-webkit-scrollbar-thumb": { backgroundColor: theme.palette.grey[500], borderRadius: 6 },
  },
}));

export default function BoardScrollbar({ boardRef, content }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const scrollbarRef = useRef(null);
  const [layout, setLayout] = useState({ visible: false, left: 0, width: 0, contentWidth: 0 });

  useLayoutEffect(() => {
    const board = boardRef.current;
    const scrollbar = scrollbarRef.current;
    if (!board || !scrollbar) return;

    const measure = () => {
      const rect = board.getBoundingClientRect();
      let top = 0;
      let bottom = window.innerHeight;
      // The app scrolls inside its main content area, rather than the window.
      for (let parent = board.parentElement; parent; parent = parent.parentElement) {
        if (/(auto|scroll|hidden|clip|overlay)/.test(window.getComputedStyle(parent).overflowY)) {
          const bounds = parent.getBoundingClientRect();
          top = Math.max(top, bounds.top + parent.clientTop);
          bottom = Math.min(bottom, bounds.top + parent.clientTop + parent.clientHeight);
        }
      }
      const left = Math.max(0, rect.left);
      const width = Math.max(0, Math.min(window.innerWidth, rect.right) - left);
      const next = {
        visible: width > 0 && board.scrollWidth > board.clientWidth + 1 &&
          rect.top < bottom && rect.bottom > top && rect.bottom > bottom,
        left,
        width,
        contentWidth: board.scrollWidth - board.clientWidth + width,
      };
      setLayout((previous) => Object.keys(next).every((key) => previous[key] === next[key]) ? previous : next);
    };
    const syncFromBoard = () => {
      if (scrollbar.scrollLeft !== board.scrollLeft) scrollbar.scrollLeft = board.scrollLeft;
    };
    const syncFromScrollbar = () => {
      if (scrollbar.clientWidth && board.scrollLeft !== scrollbar.scrollLeft) board.scrollLeft = scrollbar.scrollLeft;
    };
    board.addEventListener("scroll", syncFromBoard, { passive: true });
    scrollbar.addEventListener("scroll", syncFromScrollbar, { passive: true });
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    const observer = window.ResizeObserver ? new window.ResizeObserver(measure) : null;
    if (observer) {
      observer.observe(board);
      // Column changes can affect scrollWidth without resizing the board itself.
      Array.from(board.children).forEach((list) => observer.observe(list));
    }
    measure();
    return () => {
      board.removeEventListener("scroll", syncFromBoard);
      scrollbar.removeEventListener("scroll", syncFromScrollbar);
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
      if (observer) observer.disconnect();
    };
  }, [boardRef, content]);

  useLayoutEffect(() => {
    // Restore the scroll offset after a previously hidden scrollbar becomes visible.
    if (layout.visible && boardRef.current && scrollbarRef.current) {
      scrollbarRef.current.scrollLeft = boardRef.current.scrollLeft;
    }
  }, [boardRef, layout]);

  return (
    <div ref={scrollbarRef} className={classes.scrollbar} tabIndex={0}
      role="region" aria-label={t("tasks.scrollBoard")}
      style={{ display: layout.visible ? "block" : "none", left: layout.left, width: layout.width }}>
      <div style={{ width: layout.contentWidth, height: 1 }} />
    </div>
  );
}
